import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import './Regions.css';
import apiService from '../../services/apiService';
import { 
  hasPermission, 
  hasAnyPermission, 
  filterDataByUserAccess,
  PERMISSION_CODES 
} from '../../utils/permissionUtils';
import { PermissionGuard } from '../../components/PermissionGuard';
import notificationService from '../../CommanService/notificationService';

const Regions = () => {
  const { user } = useSelector(state => state.auth);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [viewRegion, setViewRegion] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editRegion, setEditRegion] = useState(null);

  // Check if user has permission to access regions
  const canViewRegions = hasPermission(user, PERMISSION_CODES.REGION_VIEW) || 
                        hasPermission(user, PERMISSION_CODES.REGION_MANAGEMENT);
  const canAddRegions = hasPermission(user, PERMISSION_CODES.REGION_ADD) || 
                       hasPermission(user, PERMISSION_CODES.REGION_MANAGEMENT);
  const canEditRegions = hasPermission(user, PERMISSION_CODES.REGION_EDIT) || 
                        hasPermission(user, PERMISSION_CODES.REGION_MANAGEMENT);
  const canDeleteRegions = hasPermission(user, PERMISSION_CODES.REGION_DELETE) || 
                          hasPermission(user, PERMISSION_CODES.REGION_MANAGEMENT);

  useEffect(() => {
    fetchRegions();
  }, []);

  const fetchRegions = async () => {
    try {
      const response = await apiService.GetAllRegions();
      // Filter regions based on user's access
      const filteredRegions = filterDataByUserAccess(response.data, user);
      setRegions(filteredRegions);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch regions');
      setLoading(false);
    }
  };

  // Filtered regions by search
  const filteredRegions = regions.filter(region =>
    (region.region_name || '').toLowerCase().includes(search.toLowerCase())
  );

  const handleViewRegion = (region) => {
    setViewRegion(region);
  };
  const handleCloseViewModal = () => {
    setViewRegion(null);
  };

  const handleDeleteRegion = async (id) => {
    if (!canDeleteRegions) {
      alert('You do not have permission to delete regions.');
      return;
    }
    
    try {
      await apiService.DeleteRegionByID(id);
      notificationService.showSuccess('Region deleted successfully!');
      fetchRegions();
    } catch (err) {
      notificationService.showError('Failed to delete region.');
    }
  };

  const handleEditRegion = (region) => {
    if (!canEditRegions) {
      alert('You do not have permission to edit regions.');
      return;
    }
    setEditRegion(region);
    setShowAddModal(true);
  };

  const handleAddRegion = async (data) => {
    if (!canAddRegions) {
      alert('You do not have permission to add regions.');
      return;
    }
    
    try {
      await apiService.AddAndUpdateRegion({ id: editRegion ? editRegion.id : 0, ...data });
      notificationService.showSuccess(editRegion ? 'Region updated successfully!' : 'Region added successfully!');
      setShowAddModal(false);
      setEditRegion(null);
      fetchRegions();
    } catch (err) {
      notificationService.showError('Failed to add region.');
    }
  };

  const cssLoader = (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      background: 'rgba(255,255,255,0.8)',
      zIndex: 9999,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '20px'
    }}>
      <div className="bouncing-loader">
        <div></div><div></div><div></div>
      </div>
      <p style={{ 
        color: '#333', 
        fontSize: '18px', 
        fontWeight: '600',
        margin: 0,
        textAlign: 'center',
        textShadow: '0 1px 2px rgba(0,0,0,0.1)'
      }}>
        Data is loading, please wait...
      </p>
      <style>
        {`
        .bouncing-loader {
          display: flex;
          align-items: flex-end;
          justify-content: center;
          height: 60px;
          gap: 10px;
        }
        .bouncing-loader div {
          width: 18px;
          height: 18px;
          background: linear-gradient(135deg, #ff6600 40%, #ffa500 100%);
          border-radius: 50%;
          animation: bouncing-loader 0.6s infinite alternate;
          box-shadow: 0 4px 16px rgba(255, 102, 0, 0.2);
        }
        .bouncing-loader div:nth-child(2) {
          animation-delay: 0.2s;
        }
        .bouncing-loader div:nth-child(3) {
          animation-delay: 0.4s;
        }
        @keyframes bouncing-loader {
          to {
            transform: translateY(-30px) scale(1.1);
            box-shadow: 0 16px 32px rgba(255, 102, 0, 0.3);
          }
        }
        `}
      </style>
    </div>
  );

  const tableCircleLoader = (
    <tr>
      <td colSpan={7} style={{ textAlign: 'center', padding: 32 }}>
        <div className="circle-table-loader">
          <div></div>
        </div>
        <style>
          {`
          .circle-table-loader {
            display: inline-block;
            width: 40px;
            height: 40px;
          }
          .circle-table-loader div {
            box-sizing: border-box;
            display: block;
            width: 100%;
            height: 100%;
            border: 4px solid #ff6600;
            border-radius: 50%;
            border-top: 4px solid transparent;
            animation: circle-spin 0.8s linear infinite;
          }
          @keyframes circle-spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          `}
        </style>
      </td>
    </tr>
  );

  return (
    <div className="regions-container">
      {loading && cssLoader}
      <div className="regions-header">
        <input
          type="text"
          className="regions-search"
          placeholder="Search by Regions"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ width: '350px', marginLeft: 0, marginRight: 16 }}
        />
        <PermissionGuard permission={PERMISSION_CODES.REGION_ADD} fallback={null}>
          <button className="add-region-btn" onClick={() => setShowAddModal(true)}>Add Region</button>
        </PermissionGuard>
      </div>
      <div className="regions-table-wrapper">
        <table className="regions-table">
          <thead>
            <tr>
              <th>Regions</th>
              <th>Created On</th>
              <th>Created By</th>
              <th>Modified On</th>
              <th>Modified By</th>
              <th>Status</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {error ? (
              <tr><td colSpan={7} style={{ color: 'red', textAlign: 'center' }}>{error}</td></tr>
            ) : filteredRegions.length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center' }}>No regions found.</td></tr>
            ) : (
              filteredRegions.map(region => (
                <tr key={region.id}>
                  <td>{region.region_name}</td>
                  <td>{region.created_on ? new Date(region.created_on).toLocaleDateString('en-US') : ''}</td>
                  <td>{region.created_by_name || region.created_by}</td>
                  <td>{region.last_modified_on ? new Date(region.last_modified_on).toLocaleDateString('en-US') : ''}</td>
                  <td>{region.last_modified_by_name || region.last_modified_by}</td>
                  <td>
                    {region.is_active ? (
                      <span className="status-badge active">Active</span>
                    ) : (
                      <span className="status-badge inactive">Inactive</span>
                    )}
                  </td>
                  <td>
                    <PermissionGuard permission={PERMISSION_CODES.REGION_VIEW} fallback={null}>
                      <button className="action-btn view" title="View" onClick={() => handleViewRegion(region)}><span role="img" aria-label="view">👁️</span></button>
                    </PermissionGuard>
                    <PermissionGuard permission={PERMISSION_CODES.REGION_EDIT} fallback={null}>
                      <button className="action-btn edit" title="Edit" onClick={() => handleEditRegion(region)}><span role="img" aria-label="edit">✏️</span></button>
                    </PermissionGuard>
                    <PermissionGuard permission={PERMISSION_CODES.REGION_DELETE} fallback={null}>
                      <button className="action-btn delete" title="Delete" onClick={() => handleDeleteRegion(region.id)}><span role="img" aria-label="delete">🗑️</span></button>
                    </PermissionGuard>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {/* Pagination controls placeholder */}
      <div className="regions-pagination">
        <div>Rows per page: <select><option>10</option><option>25</option><option>50</option></select></div>
        <div>1-4 of {filteredRegions.length}</div>
        <div><button disabled>{'<'}</button><button disabled>{'>'}</button></div>
      </div>
      {viewRegion && <ViewRegionModal region={viewRegion} onClose={handleCloseViewModal} />}
      {showAddModal && <AddRegionModal open={showAddModal} onClose={() => { setShowAddModal(false); setEditRegion(null); }} onSubmit={handleAddRegion} editRegion={editRegion} />}
    </div>
  );
};

const ViewRegionModal = ({ region, onClose }) => {
  if (!region) return null;
  return (
    <div className="modal-backdrop" style={{zIndex: 1000, position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
      <div style={{display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 0}}>
        <div style={{background: '#3a3326', color: '#fff', borderTopLeftRadius: 16, borderTopRightRadius: 16, padding: '24px 48px 32px 48px', width: 600, minHeight: 80, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative'}}>
          {region.is_active ? (
            <span className="status-badge active" style={{position: 'absolute', left: 24, top: 24}}>Active</span>
          ) : (
            <span className="status-badge inactive" style={{position: 'absolute', left: 24, top: 24}}>Inactive</span>
          )}
          <span style={{fontWeight: 700, fontSize: 36, marginTop: 12, letterSpacing: 1}}>{region.region_name}</span>
          <button onClick={onClose} style={{position: 'absolute', right: 16, top: 16, background: 'white', color: '#3a3326', border: 'none', borderRadius: '50%', width: 32, height: 32, fontSize: 20, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.08)'}}>×</button>
        </div>
        <div style={{background: '#fff', color: '#222', borderBottomLeftRadius: 16, borderBottomRightRadius: 16, width: 600, padding: '32px 32px', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 24, boxSizing: 'border-box'}}>
          <div style={{display: 'flex', flexDirection: 'column', gap: 8, minWidth: 120}}>
            <div style={{fontWeight: 600}}>Created On</div>
            <div>{region.created_on ? new Date(region.created_on).toLocaleDateString('en-US') : ''}</div>
          </div>
          <div style={{display: 'flex', flexDirection: 'column', gap: 8, minWidth: 120}}>
            <div style={{fontWeight: 600}}>Created By</div>
            <div>{region.created_by_name || region.created_by}</div>
          </div>
          <div style={{display: 'flex', flexDirection: 'column', gap: 8, minWidth: 120}}>
            <div style={{fontWeight: 600}}>Modified On</div>
            <div>{region.last_modified_on ? new Date(region.last_modified_on).toLocaleDateString('en-US') : ''}</div>
          </div>
          <div style={{display: 'flex', flexDirection: 'column', gap: 8, minWidth: 120}}>
            <div style={{fontWeight: 600}}>Modified By</div>
            <div>{region.last_modified_by_name || region.last_modified_by}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const AddRegionModal = ({ open, onClose, onSubmit, editRegion }) => {
  const [regionName, setRegionName] = useState('');
  const [isActive, setIsActive] = useState('true');

  useEffect(() => {
    if (open) {
      if (editRegion) {
        setRegionName(editRegion.region_name || '');
        setIsActive(editRegion.is_active ? 'true' : 'false');
      } else {
        setRegionName('');
        setIsActive('true');
      }
    }
  }, [open, editRegion]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({ region_name: regionName, is_active: isActive === 'true' });
  };

  if (!open) return null;
  return (
    <div className="modal-backdrop" style={{zIndex: 1000, position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', background: 'rgba(0,0,0,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
      <div style={{background: '#fff', borderRadius: 16, width: 400, boxShadow: '0 2px 16px rgba(0,0,0,0.12)', padding: 0}}>
        <div style={{background: '#3a3326', color: '#fff', borderTopLeftRadius: 16, borderTopRightRadius: 16, padding: '24px 32px 20px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
          <h2 style={{margin: 0, fontSize: 22, fontWeight: 700}}>{editRegion ? 'Update Region' : 'Add Region'}</h2>
          <button onClick={onClose} style={{background: 'white', color: '#3a3326', border: 'none', borderRadius: '50%', width: 32, height: 32, fontSize: 20, cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.08)'}}>×</button>
        </div>
        <form onSubmit={handleSubmit} style={{padding: '28px 32px 24px 32px', display: 'flex', flexDirection: 'column', gap: 20}}>
          <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
            <label style={{fontWeight: 600}}>Region Name<span style={{color:'red'}}>*</span></label>
            <input type="text" value={regionName} onChange={e => setRegionName(e.target.value)} required style={{padding: '10px 12px', borderRadius: 8, border: '1px solid #eee', fontSize: 16}} />
          </div>
          <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
            <label style={{fontWeight: 600}}>Status<span style={{color:'red'}}>*</span></label>
            <select value={isActive} onChange={e => setIsActive(e.target.value)} required style={{padding: '10px 12px', borderRadius: 8, border: '1px solid #eee', fontSize: 16}}>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <div style={{display: 'flex', gap: 16, marginTop: 12}}>
            <button type="button" onClick={onClose} style={{flex: 1, border: '1.5px solid #ff6600', color: '#ff6600', background: '#fff', borderRadius: 8, padding: '12px 0', fontWeight: 600, fontSize: 16}}>Cancel</button>
            <button type="submit" style={{flex: 1, background: '#ff6600', color: '#fff', border: 'none', borderRadius: 8, padding: '12px 0', fontWeight: 600, fontSize: 16}}>{editRegion ? 'Update Region' : 'Add Region'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Regions; 